// @ts-strict-ignore
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@styleseat/ui';
import { LocationRecord } from '../../store/Location.model';
import { IRootDispatch, RootState } from '../../store/models';
import { HeaderContainer } from '../consumer/header';
import { FindSomeoneNewSection } from '../shared/providerProfile/InactiveProfile/FindSomeoneNewSection';
import { RelatedNearbyPros } from '../shared/providerProfile/InactiveProfile/RelatedNearbyPros';
import useSearchTiles from '../shared/providerProfile/InactiveProfile/useSearchTiles';
import {
  Stack,
  HeaderText,
  ModalBox,
  Absolute,
} from '../ui';
import RelatedSalons from '../../modules/consumer/search/RelatedSalons';
import { useLinkTo, useMount } from '../../hooks';
import { toSlugCase } from '../../modules/stringUtils';
import { SEARCH_PARAM_RESET_CODE } from '../../modules/consumer/search/Constants';
import type { ISearchResult } from '../../modules/consumer/search/Search';
import Footer from '../shared/Footer/FooterContainer';
import { SEARCH_ROUTE } from '../../route-names';

const NotFoundPage = () => {
  const dispatch = useDispatch<IRootDispatch>();
  const linkTo = useLinkTo();
  const { handleTileClick, tiles } = useSearchTiles();
  const searchLocation: LocationRecord = useSelector<RootState, LocationRecord>(
    state => state.search,
  );
  const deviceLocation: LocationRecord = useSelector<RootState, LocationRecord>(
    state => state.location,
  );
  const [location, setLocation] = useState<LocationRecord | null>(
    searchLocation.address ? searchLocation : deviceLocation,
  );
  const [queryText, setQueryText] = useState<string>('');
  const [suggestions, setSuggestions] = useState<ISearchResult[]>([]);
  const prevSearchParams = useSelector<RootState, any>(state => state.search);
  const searchParams = useMemo(() => ({
    ...prevSearchParams,
    loc: toSlugCase(location?.address),
    lat: location?.latitude ?? SEARCH_PARAM_RESET_CODE,
    lon: location?.longitude ?? SEARCH_PARAM_RESET_CODE,
    placeId: location?.placeId ?? SEARCH_PARAM_RESET_CODE,
    q: queryText,
    source: 'inactive_profile',
  }), [
    prevSearchParams,
    queryText,
    location,
  ]);

  const handleSearchSubmit = (query: string): void => {
    if (location?.address) {
      linkTo(SEARCH_ROUTE, {
        ...searchParams,
        q: query,
      });
    } else {
      setQueryText(query);
    }
  };

  useMount(async () => {
    dispatch.loader.setIsLoading(false);
    const relatedSalons = await RelatedSalons.getRelatedSalons('Haircut', undefined);
    setSuggestions(relatedSalons?.results);
  });

  return (
    <Absolute all={0}>
      <HeaderContainer />
      <ModalBox scrollable padding={0} flexGrow={1}>
        <Stack space={20} flex={1}>
          <FindSomeoneNewSection
            onSubmit={handleSearchSubmit}
            searchParams={searchParams}
            tiles={tiles}
            location={location}
            onTileClick={handleTileClick}
            onUpdateLocation={setLocation}
            onUpdateQuery={setQueryText}
            title={(
              <Stack space={20} padding={20}>
                <HeaderText textAlign="center">
                  {'Oops, we can\'t seem to find the page you\'re looking for.'}
                </HeaderText>
                <HeaderText level={2} textAlign="center">
                  Search StyleSeat to find your next service.
                </HeaderText>
              </Stack>
            )}
          />
          {suggestions.length > 0 && (
            <RelatedNearbyPros
              proSuggestions={suggestions}
            />
          )}
        </Stack>
        <Box alignSelf="flex-end" width="100%">
          <Footer />
        </Box>
      </ModalBox>
    </Absolute>
  );
};

export default NotFoundPage;
